import { Controller } from "@hotwired/stimulus"

const EVERY = {
  daily: "every day", weekly: "every week", monthly: "every month",
  quarterly: "every 3 months", half_yearly: "every 6 months", annually: "every year"
}

// The add / edit expense dialog. Picking a type fills in the cadence, the placeholder name, the TACOS switch and a hint;
// a live sentence at the bottom says exactly what will be deducted and when, so there is no guessing about the P&L.
export default class extends Controller {
  static targets = ["type", "cadence", "name", "amount", "hint", "ads", "start", "until", "untilWrap", "startLabel", "summary", "split", "symbol"]
  static values = { currency: String, types: Array, reportingSymbol: String, reportingCode: String }

  connect() {
    this.syncCadence()
    this.summarize()
  }

  pickType(event) {
    const type = this.typesValue.find((t) => t.key === event.target.dataset.key)
    if (!type) return

    this.nameTarget.placeholder = type.key
    this.hintTarget.textContent = type.hint
    this.adsTarget.checked = !!type.advertising
    this.cadenceTargets.forEach((radio) => { radio.checked = radio.value === type.recurrence })
    this.syncCadence()
    this.summarize()
  }

  pickCadence() {
    this.syncCadence()
    this.summarize()
  }

  get recurrence() {
    return this.cadenceTargets.find((radio) => radio.checked)?.value || "one_time"
  }

  // A one-time cost has a single date and no end; a recurring one starts somewhere and may stop.
  syncCadence() {
    const oneTime = this.recurrence === "one_time"
    this.untilWrapTarget.hidden = oneTime
    this.startLabelTarget.textContent = oneTime ? "Charged on" : "First charged on"
    if (oneTime) this.untilTarget.value = ""
  }

  // How many entries the chosen scope creates: marketplaces x SKUs. The amount is split equally between them.
  get parts() {
    const count = (name) => parseInt(this.element.querySelector(`[data-multi-picker-name-value="${name}"]`)?.dataset.count || "1", 10) || 1
    return { markets: count("marketplaces"), skus: count("skus") }
  }

  // An expense is stored in its marketplace's own currency and never converted, so the amount's symbol follows the
  // marketplaces picked (all of them when none is ticked). Mixed currencies cannot share one amount.
  syncCurrency() {
    const picker = this.element.querySelector('[data-multi-picker-name-value="marketplaces"]')
    if (!picker || !this.hasSymbolTarget) return false

    const boxes = [...picker.querySelectorAll("input[type=checkbox]")]
    const ticked = boxes.filter((box) => box.checked)
    const chosen = ticked.length ? ticked : boxes.filter((box) => box.closest("li").dataset.allowed !== "0")
    const symbols = [...new Set(chosen.map((box) => box.dataset.currency).filter(Boolean))]
    const mixed = symbols.length > 1
    const symbol = mixed ? this.reportingSymbolValue : symbols[0]
    if (symbol) {
      this.currencyValue = symbol
      this.symbolTarget.textContent = symbol
    }
    return mixed
  }

  recount() {
    if (!this.hasSplitTarget) return

    if (this.syncCurrency()) {
      this.splitTarget.hidden = false
      this.splitTarget.textContent = `Amounts are in ${this.reportingCodeValue}, split equally and converted for each marketplace.`
      return
    }

    const { markets, skus } = this.parts
    const total = markets * skus
    const amount = parseFloat(this.amountTarget.value)
    this.splitTarget.hidden = total < 2
    if (total < 2) return

    const scope = [markets > 1 ? `${markets} marketplaces` : null, skus > 1 ? `${skus} ASINs` : null].filter(Boolean).join(" x ")
    const each = amount ? ` (about ${this.currencyValue}${(amount / total).toLocaleString("en-IN", { maximumFractionDigits: 2 })} each)` : ""
    this.splitTarget.textContent = `Split equally across ${scope}${each}.`
  }

  summarize() {
    this.recount()
    const amount = parseFloat(this.amountTarget.value)
    const start = this.startTarget.value ? new Date(`${this.startTarget.value}T00:00:00`) : null
    if (!amount || !start) {
      this.summaryTarget.textContent = ""
      return
    }

    const money = `${this.currencyValue}${amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
    const date = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    let text = `${money} is deducted on ${date(start)}`
    if (this.recurrence !== "one_time") {
      text += `, then ${EVERY[this.recurrence]}`
      text += this.untilTarget.value ? ` until ${date(new Date(`${this.untilTarget.value}T00:00:00`))}.` : " with no end date."
    } else {
      text += "."
    }
    this.summaryTarget.textContent = text
  }
}
